/* ============================================================
   spring-hero — crossfade hero slideshow.
   Self-contained custom element. Slides are absolutely stacked
   and fade between each other; dots/progress + optional autoplay
   drive navigation. Degrades to a static single slide when only
   one block is present (dots/autoplay are skipped in the Liquid).

   The play/pause toggle is a VIDEO control: it only appears while
   the active slide is a video, and pausing it both pauses the
   video and holds the slideshow on that slide. Works on every
   breakpoint (mobile + desktop) and for single-slide video heroes.
   ============================================================ */

class SpringHero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.spring-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.spring-hero__dot'));
    this.segments = Array.from(this.querySelectorAll('.spring-hero__progress-segment'));
    this.viewport = this.querySelector('.spring-hero__viewport');
    this.toggle = this.querySelector('.spring-hero__toggle');

    this.index = this.slides.findIndex((s) => s.classList.contains('is-active'));
    if (this.index < 0) this.index = 0;

    // When the user pauses the video, autoplay rotation stays off until resumed.
    this.userPaused = false;

    // Video play/pause control — set up first so it works even when there is
    // only one slide (a single-slide video hero still needs the control).
    this.setupVideoToggle();

    if (this.slides.length <= 1) return;

    this.autoplay = this.dataset.autoplay === 'true';
    this.delay = Math.max(2000, parseInt(this.dataset.autoplayDelay, 10) * 1000 || 6000);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timer = null;

    const navTo = (i) => {
      this.goTo(i);
      this.restart();
    };
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => navTo(i)));
    this.segments.forEach((seg, i) => seg.addEventListener('click', () => navTo(i)));

    this.bindSwipe();

    // Pause autoplay on hover / when the tab is hidden / off-screen.
    this.addEventListener('pointerenter', () => this.stop());
    this.addEventListener('pointerleave', () => this.start());

    // Mirror the hover pause for keyboard users.
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.start();
    });

    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stop();
      } else {
        this.start();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          this.onScreen = entries[0].isIntersecting;
          if (this.onScreen) {
            this.start();
          } else {
            this.stop();
          }
        },
        { threshold: 0.2 }
      );
      this.io.observe(this);
    } else {
      this.onScreen = true;
    }

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    if (this.io) this.io.disconnect();
  }

  goTo(next) {
    if (next === this.index) return;
    this.setActive(next);
  }

  advance() {
    this.setActive((this.index + 1) % this.slides.length);
  }

  retreat() {
    this.setActive((this.index - 1 + this.slides.length) % this.slides.length);
  }

  setActive(next) {
    this.slides[this.index].classList.remove('is-active');
    this.slides[this.index].setAttribute('aria-hidden', 'true');
    // Inactive slides are visually hidden (opacity 0) but stay in the DOM;
    // inert removes their CTA links from the tab order / accessibility tree.
    this.slides[this.index].setAttribute('inert', '');
    if (this.dots[this.index]) {
      this.dots[this.index].classList.remove('is-active');
      this.dots[this.index].removeAttribute('aria-current');
    }
    if (this.segments[this.index]) {
      this.segments[this.index].classList.remove('is-active');
      this.segments[this.index].removeAttribute('aria-current');
    }

    this.index = next;

    this.slides[this.index].classList.add('is-active');
    this.slides[this.index].setAttribute('aria-hidden', 'false');
    this.slides[this.index].removeAttribute('inert');
    if (this.dots[this.index]) {
      this.dots[this.index].classList.add('is-active');
      this.dots[this.index].setAttribute('aria-current', 'true');
    }
    if (this.segments[this.index]) {
      this.segments[this.index].classList.add('is-active');
      this.segments[this.index].setAttribute('aria-current', 'true');
    }

    // The toggle follows the active slide: shown only on video slides.
    this.syncToggle();
  }

  // --- Video play/pause control --------------------------------------------

  // The <video> on the currently active slide, if any.
  activeVideo() {
    const slide = this.slides[this.index];
    return slide ? slide.querySelector('video') : null;
  }

  setupVideoToggle() {
    if (!this.toggle) return;
    this.toggle.addEventListener('click', () => this.toggleVideo());
    this.syncToggle();
  }

  // Show the toggle only when the active slide is a video; reset it to "playing".
  syncToggle() {
    if (!this.toggle) return;
    const slide = this.slides[this.index];
    const isVideo = !!(slide && slide.dataset.hasVideo === 'true');
    this.toggle.hidden = !isVideo;
    if (isVideo && !this.userPaused) {
      this.toggle.dataset.state = 'playing';
      this.toggle.setAttribute('aria-label', 'Pause video');
    }
  }

  toggleVideo() {
    if (!this.toggle) return;
    const video = this.activeVideo();
    const paused = this.toggle.dataset.state === 'paused';

    if (paused) {
      // Resume the video and the slideshow rotation.
      if (video) {
        const playback = video.play();
        if (playback && playback.catch) playback.catch(() => {});
        video.dataset.a11yPriorityPause = 'false';
      }
      this.toggle.dataset.state = 'playing';
      this.toggle.setAttribute('aria-label', 'Pause video');
      this.userPaused = false;
      this.start();
    } else {
      // Pause the video and hold the slideshow on this slide.
      // a11yPriorityPause stops background-video.js's intersection observer
      // from auto-resuming the video when it scrolls back into view.
      if (video) {
        video.pause();
        video.dataset.a11yPriorityPause = 'true';
      }
      this.toggle.dataset.state = 'paused';
      this.toggle.setAttribute('aria-label', 'Play video');
      this.userPaused = true;
      this.stop();
    }
  }

  // --- Swipe ----------------------------------------------------------------

  // Touch + pointer swipe. One gesture = one transition (debounced via the
  // start/end pairing). Natural convention: swipe LEFT advances to NEXT,
  // swipe RIGHT goes to PREVIOUS (content follows the finger).
  bindSwipe() {
    const target = this.viewport || this;
    const THRESHOLD = 40;
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const onStart = (x, y) => {
      startX = x;
      startY = y;
      tracking = true;
    };

    const onEnd = (x, y) => {
      if (!tracking) return;
      tracking = false;
      const dx = x - startX;
      const dy = y - startY;
      // Ignore vertical scrolls and small jitters.
      if (Math.abs(dx) <= THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
      if (dx > 0) {
        this.retreat();
      } else {
        this.advance();
      }
      this.restart();
    };

    target.addEventListener(
      'touchstart',
      (e) => {
        const t = e.changedTouches[0];
        onStart(t.clientX, t.clientY);
      },
      { passive: true }
    );
    target.addEventListener(
      'touchend',
      (e) => {
        const t = e.changedTouches[0];
        onEnd(t.clientX, t.clientY);
      },
      { passive: true }
    );

    // Pointer fallback (e.g. mouse drag / pens). Skip touch pointers so we
    // don't double-handle alongside the touch events above.
    target.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'touch') return;
      onStart(e.clientX, e.clientY);
    });
    target.addEventListener('pointerup', (e) => {
      if (e.pointerType === 'touch') return;
      onEnd(e.clientX, e.clientY);
    });
  }

  // --- Autoplay -------------------------------------------------------------

  start() {
    if (!this.autoplay || this.reduceMotion || this.timer || this.userPaused) return;
    if (this.onScreen === false || document.hidden) return;
    this.timer = window.setInterval(() => this.advance(), this.delay);
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    this.stop();
    this.start();
  }
}

if (!customElements.get('spring-hero')) {
  customElements.define('spring-hero', SpringHero);
}
